'use client';

import { useEffect, useRef } from 'react';

import { cn } from '@commenda/utils';

import { ThinkingOrbHelper } from './thinking-orb-helper';
import { ThinkingOrbRenderer } from './thinking-orb-renderer';

interface ThinkingOrbProps {
  /** Rendered size in px. Below 32px the armillary rings are dropped. */
  size?: number;
  className?: string;
}

/**
 * The chat's thinking indicator: three typing dots revolve and merge, breathe out into a globe of
 * sand that settles into a brand-blue gradient sphere inside armillary rings, then breathe back into
 * three dots. Its light is in step with the "Thinking…" Shimmer beside it. Colours come from the
 * fg-brand tokens; reduced motion shows a still frame.
 */
export function ThinkingOrb({ size = 40, className }: ThinkingOrbProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;

    const dpr = Math.min(3, window.devicePixelRatio || 1);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const styles = getComputedStyle(canvas);
    const ink = ThinkingOrbHelper.hexToRgb(
      styles.getPropertyValue('--color-fg-brand'),
      [2, 110, 185]
    );
    const deep = ThinkingOrbHelper.hexToRgb(
      styles.getPropertyValue('--color-fg-brand-hover'),
      [0, 90, 156]
    );
    const grains = ThinkingOrbHelper.makeGrains(
      ThinkingOrbHelper.getGrainCount(size)
    );

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      ThinkingOrbRenderer.drawFrame(
        ctx,
        size,
        grains,
        ThinkingOrbHelper.HOLD_AT + 1,
        2,
        ink,
        deep
      );
      return undefined;
    }

    let frameId = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = (now - start) / 1000;
      // the running clock starts one shimmer cycle in, so the light is in phase from the first frame
      ThinkingOrbRenderer.drawFrame(
        ctx,
        size,
        grains,
        elapsed % ThinkingOrbHelper.LOOP,
        elapsed + ThinkingOrbHelper.SHIMMER_S,
        ink,
        deep
      );
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [size]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={cn('shrink-0', className)}
      style={{ width: size, height: size }}
    />
  );
}
